'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Sparkles, Brain, ArrowLeft, Mail, Send, CheckCircle, Handshake } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';

export default function AboutPage() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const [partnerData, setPartnerData] = useState({ brandName: '', contactName: '', email: '', website: '', description: '' });
  const [partnerStatus, setPartnerStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [partnerErrorMsg, setPartnerErrorMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePartnerChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPartnerData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const callEdgeFunction = async (body: Record<string, string>) => {
    const supabase = createClient();
    const { data: { session } } = await supabase.auth.getSession();
    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!;
    const functionUrl = `${supabaseUrl}/functions/v1/send-feedback-email`;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
    const res = await fetch(functionUrl, { method: 'POST', headers, body: JSON.stringify(body) });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to send message');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');
    try {
      await callEdgeFunction(formData);
      setStatus('success');
      setFormData({ name: '', email: '', message: '' });
    } catch (err: unknown) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };

  const handlePartnerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPartnerStatus('sending');
    setPartnerErrorMsg('');
    try {
      await callEdgeFunction({ type: 'partnership', ...partnerData });
      setPartnerStatus('success');
      setPartnerData({ brandName: '', contactName: '', email: '', website: '', description: '' });
    } catch (err: unknown) {
      setPartnerStatus('error');
      setPartnerErrorMsg(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FAF7F2' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 border-b" style={{ backgroundColor: '#FAF7F2', borderColor: 'hsl(35 20% 88%)' }}>
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link href="/fashion-discovery-interface" className="flex items-center gap-2 transition-opacity hover:opacity-70">
              <AppLogo size={24} />
              <span className="font-serif-display text-lg sm:text-xl tracking-tight" style={{ color: '#1A1A1A' }}>
                Atelier.ai
              </span>
            </Link>
            <Link
              href="/fashion-discovery-interface"
              className="flex items-center gap-1.5 text-xs sm:text-sm font-medium transition-all hover:opacity-70"
              style={{ color: 'hsl(30 8% 52%)' }}
            >
              <ArrowLeft size={15} />
              <span>Back to Atelier.ai</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-20 w-full">

        {/* Hero */}
        <section className="mb-14 animate-fade-in-up stagger-1">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={16} style={{ color: '#C9A96E' }} />
            <span className="text-xs font-medium tracking-widest uppercase" style={{ color: '#C9A96E' }}>About</span>
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl mb-5 leading-tight" style={{ color: '#1A1A1A' }}>
            What is Atelier.ai?
          </h1>
          <p className="text-base sm:text-lg leading-relaxed" style={{ color: 'hsl(30 8% 38%)' }}>
            Atelier.ai is a personal styling companion that helps you discover outfits tailored to your occasion, aesthetic, and budget — without the overwhelm of endless scrolling.
          </p>
          <p className="mt-4 text-base sm:text-lg leading-relaxed" style={{ color: 'hsl(30 8% 38%)' }}>
            Tell Atelier.ai where you're going, what you're feeling, and how much you'd like to spend. In seconds, you'll receive a curated outfit recommendation complete with individual pieces and direct shop links — ready to wear.
          </p>
        </section>

        {/* Divider */}
        <div className="h-px mb-14" style={{ backgroundColor: 'hsl(35 20% 88%)' }} />

        {/* How AI works */}
        <section className="mb-14 animate-fade-in-up stagger-2">
          <div className="flex items-center gap-2 mb-4">
            <Brain size={16} style={{ color: '#C9A96E' }} />
            <span className="text-xs font-medium tracking-widest uppercase" style={{ color: '#C9A96E' }}>Powered by AI</span>
          </div>
          <h2 className="font-serif-display text-2xl sm:text-3xl mb-5" style={{ color: '#1A1A1A' }}>
            How recommendations are generated
          </h2>
          <div className="space-y-4">
            <p className="text-base leading-relaxed" style={{ color: 'hsl(30 8% 38%)' }}>
              Atelier.ai uses Google Gemini, a large language model, to interpret your style preferences and generate outfit recommendations in real time. When you complete the style wizard, your inputs — occasion, aesthetic, budget, and appearance — are combined into a detailed prompt sent to the AI.
            </p>
            <p className="text-base leading-relaxed" style={{ color: 'hsl(30 8% 38%)' }}>
              Gemini then acts as a virtual stylist, selecting complementary pieces, suggesting shop links, and writing personalised styling notes — all in one go. No pre-built outfit database, no manual curation: every recommendation is generated fresh for you.
            </p>
            <p className="text-base leading-relaxed" style={{ color: 'hsl(30 8% 38%)' }}>
              Because the AI generates responses dynamically, results may vary between sessions. Think of it as having a conversation with a stylist who brings a fresh perspective each time.
            </p>
          </div>
        </section>

        {/* Divider */}
        <div className="h-px mb-14" style={{ backgroundColor: 'hsl(35 20% 88%)' }} />

        {/* Sustainability section */}
        <section className="mb-14 animate-fade-in-up stagger-3">
          <div className="flex items-center gap-2 mb-4">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#C9A96E' }} aria-hidden="true"><path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z"/><path d="M12 6v6l4 2"/></svg>
            <span className="text-xs font-medium tracking-widest uppercase" style={{ color: '#C9A96E' }}>Sustainability</span>
          </div>
          <h2 className="font-serif-display text-2xl sm:text-3xl mb-5" style={{ color: '#1A1A1A' }}>
            Dressing well, doing good
          </h2>
          <div className="space-y-4">
            <p className="text-base leading-relaxed" style={{ color: 'hsl(30 8% 38%)' }}>
              Atelier.ai is built with sustainability at its core. Our outfit recommendations actively surface options from <strong style={{ color: '#1A1A1A' }}>B Corp certified brands</strong> — businesses that meet the highest standards of social and environmental performance — alongside pre-owned platforms like Vestiaire Collective, Depop, ThredUp, The RealReal, and Vinted.
            </p>
            <p className="text-base leading-relaxed" style={{ color: 'hsl(30 8% 38%)' }}>
              Every recommendation aims to include at least one or two pieces sourced from these channels, so you can build a wardrobe that reflects your values as much as your style. Whether it's a certified sustainable brand like Reformation, Patagonia, or Eileen Fisher, or a pre-loved find from a circular fashion platform, Atelier.ai makes conscious dressing feel effortless.
            </p>
            <p className="text-base leading-relaxed" style={{ color: 'hsl(30 8% 38%)' }}>
              We believe fashion and responsibility aren't at odds — and we're here to prove it, one outfit at a time.
            </p>
          </div>
        </section>

        {/* Divider */}
        <div className="h-px mb-14" style={{ backgroundColor: 'hsl(35 20% 88%)' }} />

        {/* Creator section */}
        <section className="mb-14 animate-fade-in-up stagger-4">
          <p className="text-xs font-medium tracking-widest uppercase mb-4" style={{ color: '#C9A96E' }}>The Creator</p>
          <div className="rounded-2xl p-6 sm:p-8 border" style={{ backgroundColor: '#FFFFFF', borderColor: 'hsl(35 20% 88%)' }}>
            <h2 className="font-serif-display text-2xl sm:text-3xl mb-2" style={{ color: '#1A1A1A' }}>
              Ahshida R
            </h2>
            <p className="text-sm mb-5" style={{ color: 'hsl(30 8% 52%)' }}>
              Hobby project · Built with curiosity &amp; care
            </p>
            <p className="text-base leading-relaxed mb-6" style={{ color: 'hsl(30 8% 38%)' }}>
              Atelier.ai is a personal hobby project born from a love of fashion and a fascination with what AI can do. It's an experiment in making style discovery feel effortless and a little more personal.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="https://www.linkedin.com/in/ahshida-r/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all hover:opacity-80 active:scale-95"
                style={{ backgroundColor: '#0A66C2', color: '#FFFFFF' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                LinkedIn
              </a>
              <a
                href="mailto:hello@siahra.co"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-all hover:opacity-80 active:scale-95"
                style={{ borderColor: 'hsl(35 20% 88%)', color: 'hsl(30 8% 38%)', backgroundColor: '#FAF7F2' }}
              >
                <Mail size={16} />
                hello@siahra.co
              </a>
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="h-px mb-14" style={{ backgroundColor: 'hsl(35 20% 88%)' }} />

        {/* Get In Touch — Partnerships */}
        <section className="mb-14 animate-fade-in-up stagger-4">
          <div className="flex items-center gap-2 mb-4">
            <Handshake size={16} style={{ color: '#C9A96E' }} />
            <span className="text-xs font-medium tracking-widest uppercase" style={{ color: '#C9A96E' }}>Get in Touch</span>
          </div>
          <h2 className="font-serif-display text-2xl sm:text-3xl mb-3" style={{ color: '#1A1A1A' }}>
            Partnerships
          </h2>
          <p className="text-base mb-2" style={{ color: 'hsl(30 8% 38%)' }}>
            We're inviting indie and local labels to join Atelier.ai <strong style={{ color: '#1A1A1A' }}>for free</strong> during our beta phase, launching <strong style={{ color: '#1A1A1A' }}>May / June 2026</strong>.
          </p>
          <p className="text-base mb-8" style={{ color: 'hsl(30 8% 52%)' }}>
            If you're a small or independent label and want your pieces featured in AI-generated outfit recommendations, we'd love to hear from you.
          </p>

          {partnerStatus === 'success' ? (
            <div className="rounded-2xl p-8 border flex flex-col items-center text-center gap-4" style={{ backgroundColor: '#FFFFFF', borderColor: 'hsl(35 20% 88%)' }}>
              <CheckCircle size={40} style={{ color: '#C9A96E' }} />
              <div>
                <p className="font-serif-display text-xl mb-1" style={{ color: '#1A1A1A' }}>Application received!</p>
                <p className="text-sm" style={{ color: 'hsl(30 8% 52%)' }}>Thanks for your interest. We'll be in touch ahead of the beta launch.</p>
              </div>
              <button
                onClick={() => setPartnerStatus('idle')}
                className="mt-2 text-xs font-medium underline transition-opacity hover:opacity-70"
                style={{ color: 'hsl(30 8% 52%)' }}
              >
                Submit another application
              </button>
            </div>
          ) : (
            <form onSubmit={handlePartnerSubmit} className="rounded-2xl p-6 sm:p-8 border space-y-5" style={{ backgroundColor: '#FFFFFF', borderColor: 'hsl(35 20% 88%)' }}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="brandName" className="block text-xs font-medium mb-1.5 tracking-wide" style={{ color: 'hsl(30 8% 38%)' }}>
                    Brand / Label Name
                  </label>
                  <input
                    id="brandName"
                    name="brandName"
                    type="text"
                    required
                    value={partnerData.brandName}
                    onChange={handlePartnerChange}
                    placeholder="Your brand name"
                    className="w-full px-4 py-2.5 rounded-lg text-sm border outline-none transition-all focus:ring-2"
                    style={{ backgroundColor: '#FAF7F2', borderColor: 'hsl(35 20% 88%)', color: '#1A1A1A' }}
                  />
                </div>
                <div>
                  <label htmlFor="contactName" className="block text-xs font-medium mb-1.5 tracking-wide" style={{ color: 'hsl(30 8% 38%)' }}>
                    Your Name
                  </label>
                  <input
                    id="contactName"
                    name="contactName"
                    type="text"
                    required
                    value={partnerData.contactName}
                    onChange={handlePartnerChange}
                    placeholder="Contact name"
                    className="w-full px-4 py-2.5 rounded-lg text-sm border outline-none transition-all focus:ring-2"
                    style={{ backgroundColor: '#FAF7F2', borderColor: 'hsl(35 20% 88%)', color: '#1A1A1A' }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="partnerEmail" className="block text-xs font-medium mb-1.5 tracking-wide" style={{ color: 'hsl(30 8% 38%)' }}>
                    Email
                  </label>
                  <input
                    id="partnerEmail"
                    name="email"
                    type="email"
                    required
                    value={partnerData.email}
                    onChange={handlePartnerChange}
                    placeholder="your@email.com"
                    className="w-full px-4 py-2.5 rounded-lg text-sm border outline-none transition-all focus:ring-2"
                    style={{ backgroundColor: '#FAF7F2', borderColor: 'hsl(35 20% 88%)', color: '#1A1A1A' }}
                  />
                </div>
                <div>
                  <label htmlFor="website" className="block text-xs font-medium mb-1.5 tracking-wide" style={{ color: 'hsl(30 8% 38%)' }}>
                    Website or Instagram <span style={{ color: 'hsl(30 8% 65%)' }}>(optional)</span>
                  </label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    value={partnerData.website}
                    onChange={handlePartnerChange}
                    placeholder="https:// or @handle"
                    className="w-full px-4 py-2.5 rounded-lg text-sm border outline-none transition-all focus:ring-2"
                    style={{ backgroundColor: '#FAF7F2', borderColor: 'hsl(35 20% 88%)', color: '#1A1A1A' }}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="description" className="block text-xs font-medium mb-1.5 tracking-wide" style={{ color: 'hsl(30 8% 38%)' }}>
                  Tell us about your brand
                </label>
                <textarea
                  id="description"
                  name="description"
                  required
                  rows={4}
                  value={partnerData.description}
                  onChange={handlePartnerChange}
                  placeholder="What do you make, who is it for, and what makes your label special?"
                  className="w-full px-4 py-2.5 rounded-lg text-sm border outline-none transition-all focus:ring-2 resize-none"
                  style={{ backgroundColor: '#FAF7F2', borderColor: 'hsl(35 20% 88%)', color: '#1A1A1A' }}
                />
              </div>

              {partnerStatus === 'error' && (
                <p className="text-xs" style={{ color: '#c0392b' }}>{partnerErrorMsg}</p>
              )}

              <button
                type="submit"
                disabled={partnerStatus === 'sending'}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all hover:opacity-80 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#C9A96E', color: '#FFFFFF' }}
              >
                <Handshake size={14} />
                {partnerStatus === 'sending' ? 'Sending…' : 'Apply for beta partnership'}
              </button>
            </form>
          )}
        </section>

        {/* Divider */}
        <div className="h-px mb-14" style={{ backgroundColor: 'hsl(35 20% 88%)' }} />

        {/* Feedback Form */}
        <section className="animate-fade-in-up stagger-4">
          <div className="flex items-center gap-2 mb-4">
            <Send size={16} style={{ color: '#C9A96E' }} />
            <span className="text-xs font-medium tracking-widest uppercase" style={{ color: '#C9A96E' }}>Get in Touch</span>
          </div>
          <h2 className="font-serif-display text-2xl sm:text-3xl mb-3" style={{ color: '#1A1A1A' }}>
            Send feedback
          </h2>
          <p className="text-base mb-8" style={{ color: 'hsl(30 8% 52%)' }}>
            Have a thought, suggestion, or just want to say hello? I'd love to hear from you.
          </p>

          {status === 'success' ? (
            <div className="rounded-2xl p-8 border flex flex-col items-center text-center gap-4" style={{ backgroundColor: '#FFFFFF', borderColor: 'hsl(35 20% 88%)' }}>
              <CheckCircle size={40} style={{ color: '#C9A96E' }} />
              <div>
                <p className="font-serif-display text-xl mb-1" style={{ color: '#1A1A1A' }}>Message sent!</p>
                <p className="text-sm" style={{ color: 'hsl(30 8% 52%)' }}>Thanks for reaching out. I'll get back to you soon.</p>
              </div>
              <button
                onClick={() => setStatus('idle')}
                className="mt-2 text-xs font-medium underline transition-opacity hover:opacity-70"
                style={{ color: 'hsl(30 8% 52%)' }}
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="rounded-2xl p-6 sm:p-8 border space-y-5" style={{ backgroundColor: '#FFFFFF', borderColor: 'hsl(35 20% 88%)' }}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-xs font-medium mb-1.5 tracking-wide" style={{ color: 'hsl(30 8% 38%)' }}>
                    Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    className="w-full px-4 py-2.5 rounded-lg text-sm border outline-none transition-all focus:ring-2"
                    style={{ backgroundColor: '#FAF7F2', borderColor: 'hsl(35 20% 88%)', color: '#1A1A1A' }}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs font-medium mb-1.5 tracking-wide" style={{ color: 'hsl(30 8% 38%)' }}>
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="w-full px-4 py-2.5 rounded-lg text-sm border outline-none transition-all focus:ring-2"
                    style={{ backgroundColor: '#FAF7F2', borderColor: 'hsl(35 20% 88%)', color: '#1A1A1A' }}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="message" className="block text-xs font-medium mb-1.5 tracking-wide" style={{ color: 'hsl(30 8% 38%)' }}>
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Your thoughts, feedback, or questions…"
                  className="w-full px-4 py-2.5 rounded-lg text-sm border outline-none transition-all focus:ring-2 resize-none"
                  style={{ backgroundColor: '#FAF7F2', borderColor: 'hsl(35 20% 88%)', color: '#1A1A1A' }}
                />
              </div>

              {status === 'error' && (
                <p className="text-xs" style={{ color: '#c0392b' }}>{errorMsg}</p>
              )}

              <button
                type="submit"
                disabled={status === 'sending'}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all hover:opacity-80 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#C9A96E', color: '#FFFFFF' }}
              >
                <Send size={14} />
                {status === 'sending' ? 'Sending…' : 'Send message'}
              </button>
            </form>
          )}
        </section>

        {/* Footer CTA */}
        <div className="mt-16 text-center animate-fade-in-up stagger-5">
          <Link
            href="/fashion-discovery-interface"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all hover:opacity-80 active:scale-95"
            style={{ backgroundColor: '#C9A96E', color: '#FFFFFF' }}
          >
            <Sparkles size={15} />
            Style my outfit
          </Link>
        </div>
      </main>
    </div>
  );
}
